import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./shared/context/AuthContext";
import { ThemeProvider } from "./shared/context/ThemeContext";
import { Header, MobileNav } from "./modules/station-logbook/components/Layout";
import LoginPage from "./modules/station-logbook/pages/LoginPage";
import DashboardPage from "./modules/station-logbook/pages/DashboardPage";
import StationEntryPage from "./modules/station-logbook/pages/StationEntryPage";
import ActiveVisitPage from "./modules/station-logbook/pages/ActiveVisitPage";
import StationExitPage from "./modules/station-logbook/pages/StationExitPage";
import SupervisorDashboardPage from "./modules/station-logbook/pages/SupervisorDashboardPage";
import HistoryPage from "./modules/station-logbook/pages/HistoryPage";
import AdminPage from "./modules/station-logbook/pages/AdminPage";
import StationsManagerPage from "./modules/station-logbook/pages/StationsManagerPage";
import OfficeLocationsManagerPage from "./modules/station-logbook/pages/OfficeLocationsManagerPage";
import UsersManagerPage from "./modules/station-logbook/pages/UsersManagerPage";
import SSOStartPage from "./modules/station-logbook/pages/SSOStartPage.jsx";
import AuthCallbackPage from "./modules/station-logbook/pages/AuthCallbackPage.jsx";
import MapView from "./modules/station-logbook/pages/MapView";
import UserManualPage from "./modules/station-logbook/pages/UserManualPage";
import "leaflet/dist/leaflet.css";
import "./App.css";

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner h-8 w-8 border-[#FF6B00]" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Admin Route wrapper
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner h-8 w-8 border-[#FF6B00]" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.role !== "admin") {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Public Route wrapper (redirect to home if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner h-8 w-8 border-[#FF6B00]" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Main App Layout
const AppLayout = ({ children }) => {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pb-20 md:pb-6">{children}</main>
      <MobileNav />
    </div>
  );
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      
      {/* SSO Routes - public, handles OIDC flow */}
      <Route path="/sso/start" element={<SSOStartPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />

      {/* Protected Routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AppLayout>
              <DashboardPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/entry"
        element={
          <ProtectedRoute>
            <AppLayout>
              <StationEntryPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/visit/:visitId"
        element={
          <ProtectedRoute>
            <AppLayout>
              <ActiveVisitPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/exit/:visitId"
        element={
          <ProtectedRoute>
            <AppLayout>
              <StationExitPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supervisor"
        element={
          <ProtectedRoute>
            <AppLayout>
              <SupervisorDashboardPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/history"
        element={
          <ProtectedRoute>
            <AppLayout>
              <HistoryPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/map"
        element={
          <ProtectedRoute>
            <MapView />
          </ProtectedRoute>
        }
      />

      {/* User Manual - Public Route */}
      <Route path="/manual" element={<UserManualPage />} />
      <Route path="/help" element={<UserManualPage />} />

      {/* Admin Routes */}
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <AppLayout>
              <AdminPage />
            </AppLayout>
          </AdminRoute>
        }
      />
      <Route
        path="/admin/stations"
        element={
          <AdminRoute>
            <AppLayout>
              <StationsManagerPage />
            </AppLayout>
          </AdminRoute>
        }
      />
      <Route
        path="/admin/office-locations"
        element={
          <AdminRoute>
            <AppLayout>
              <OfficeLocationsManagerPage />
            </AppLayout>
          </AdminRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <AdminRoute>
            <AppLayout>
              <UsersManagerPage />
            </AppLayout>
          </AdminRoute>
        }
      />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Determine basename based on environment and handle production redirects
const getBasename = () => {
  const hostname = window.location.hostname;
  const pathname = window.location.pathname;
  
  // In production (stationlogbook.com), app is at /station-logbook/
  if (hostname.includes('stationlogbook.com')) {
    // If user is accessing root or a path without /station-logbook prefix,
    // redirect them to the correct path
    if (!pathname.startsWith('/station-logbook')) {
      const newPath = '/station-logbook' + (pathname === '/' ? '/login' : pathname);
      window.location.replace(newPath + window.location.search + window.location.hash);
      return '/station-logbook'; // Return basename anyway (won't render due to redirect)
    }
    return '/station-logbook';
  }
  
  // In preview/development, app is at root /
  return '/';
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter basename={getBasename()}>
          <AppRoutes />
          <Toaster
            position="top-right"
            toastOptions={{
              className: "rounded-none border-2 font-mono",
            }}
          />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
